import { NoTzDate } from '../../DateUtils';

export enum TimelineSection {
    Vitals,
    ProgressNotes,
    Mail,               // AKA: Communications
    Medications,
    Immunizations,
    Obstetrics,
    OrderResults,
    Documents,
    PatientReported,    // AKA: Health Exchange
    Telemedicine,
    Authorizations
}

export interface ITimelineSectionSetupItem {
    id: TimelineSection,
    name: string,
    screenName: string,
    enabled: string,
    sort_order: number
}

export interface ITimelineSetup
{
    range: string,
    rangeDesc: string,
    today: string,
    rangeStart: string,
    rangeEnd: string,
    securityUsersId: number,
    maxEmrPrivilege: number    
}

export interface IEncounterDate
{
    encounterDate: NoTzDate,
}

export interface ITimelineVital extends IEncounterDate
{
    vitalsId: number,
    encounterDate: NoTzDate,
    measurementSystem?: MeasurementSystem,
    heightPrimaryValue?: number,
    heightSecondaryValue?: number,
    height?: string,
    weight?: string,
    weightPrimaryValue?: number,
    weightSecondaryValue?: number,
    systolic?: number,
    diastolic?: number,
    pulse?:number,
    positionCode?:number,
    bmi?:string,
    bsa?:string,
    temperature?: number,
    respirations?: number,
    pain?: number,
    pulseOximetryId?: number,
    spO2?: number
}

export interface ITimelineProgressNote extends IEncounterDate
{
    soapNoteId: number,
    encounterDate: NoTzDate,
    soapNoteTypeDescription?: string,
    signOffName?: string,
    nursesNoteLetterId?: number
}

export interface ITimelineMail extends IEncounterDate
{
    mailId: number,
    encounterDate: NoTzDate,
    mailType: string,
    subject?: string,
    message?: string
    fromUser?: string
    toUser?: string
}

export interface ITimelineMedication
{
    prescriptionId: number,
    startDate?: NoTzDate,
    stopDate?: NoTzDate,
    shortName?: string,
    patientReported: boolean,
    prescribedBy?: string,
    drugName?: string
}

export interface ITimelineImmunization extends IEncounterDate
{
    immunizationId: number,
    encounterDate: NoTzDate,
    immunizationName?: string,
    dateGiven?: NoTzDate,
    nextDue?: string,
    location?: string,
    type?: string,
    reaction?: string,
    refused: boolean
}

export interface ITimelineObstetric extends IEncounterDate
{
    pregnancyId: number,
    encounterDate: NoTzDate,
    weeksGest: string,
    fundalHeight: string,
    edd?: string, 
    presentation?: string,
    fhr?: string,
    fetalMovement?: string,
    pretermLabor?: string,
    systolic?: number,
    diastolic?: number,
    weight?: string,
    urineAlbumin?: string,
    urineGlucose?: string,
    edemaInd?: string,
    nextAppointment?: string,
    comments?: string
}

export interface ITimelineOrderResult extends IEncounterDate
{
    encounterDate: NoTzDate,
    orderItemDescription: string,
    maxOrderDisplayLimit: number
}

export interface ITimelineDocument extends IEncounterDate
{
    imageId: number,
    encounterDate: NoTzDate,
    imagePath: string,
    imageName: string,
    isGroup: boolean,
    documentId?: number,
    reviewNeeded: boolean,
    fontColor?: string,
    fontStyle?: string
}

export interface ITimelinePatientReported extends IEncounterDate
{
    type: string,
    reportedId: number,
    encounterDate: string
}

export interface ITimelineTelemedicine extends IEncounterDate
{
    keyId: number,
    encounterDate: NoTzDate,
    startSessionDate: string,
    endSessionDate: string,
    sendMessageDate: string,
    patientSessionDate: string,
    postVisitInstructions?: string
}

export interface ITimelineManualAuthorization extends IEncounterDate
{
    authorizationId: number,
    encounterDate: NoTzDate,
    reason?: string
}
export interface ITimelineElectronicAuthorization extends IEncounterDate
{
    encounterDate: NoTzDate,
    taskItemIdType: number, 
    cmmEpaCallbackId?: number, 
    cmmEpaSendId?: number, 
    ePATaskStatus?: string, 
    drugShortName?: string, 
    providerName?: string,
    writtenDate?: string
}
export interface ITimelineAuthorizationItems
{
    manualAuthItems: ITimelineManualAuthorization[],
    electronicAuthItems: ITimelineElectronicAuthorization[]
}

export interface ITimelineSectionClick 
{
    id: TimelineSection,
    sectionId?: number | string,
    date: NoTzDate,
    html?: string,
    title?: string,
    data?: string
}

export interface IFishboneTemplateType
{
    fbSubId: number,
    templateType: string,
    templateId: number,
    templateName: string,
    globalEntityId: number,
    controlTypeId: number,
}

export interface IFishboneType
{
    fbId: number,
    fbDescription: string,
    fbSubId: number,
    fbSubLabel: string,
    fbSubDescription: string,
    wireframeId: number,
    templates?: IFishboneTemplateType[]
}

export interface IFishboneHeader
{
    fbId: number,
    fbDescription: string
}

export interface ITimelineFishboneResult extends IEncounterDate
{
    fbId: number,
    fbDescription: string,
    fbSubId: number,
    fbResultLabel: string,
    labReportDate: string,
    labReportDateTime: NoTzDate,
    resultValue: string,
    resultAbnormalFlags: string,
    unitOfMeasure: string,
    hasOrder: boolean,
    isTemplateResult: boolean,
    resultIdentifier1: number,
    resultIdentifier2: number
}

export interface IEMRSummaryDataItem {
    date: NoTzDate,
    fbResults?: ITimelineFishboneResult[],
    customResults?: ITimelineCustomResult[],
    vitals?: ITimelineVital[]
}

export interface ITimelineCustomResultHeader {
    globalEntityId: number,
    description: string
}
export interface ITimelineCustomResult {
    encounterId: number,
    globalEntityId: number,
    description: string,
    units: string,
    encounterValue: string,
    encounterDate: string
}

export interface ITimelineVitalEdit
{
    vitalsId: number,
    patientId: number,
    temperature?: number,
    heightPrimaryValue?: number,
    heightSecondaryValue?: number,
    weightPrimaryValue?: number,
    weightSecondaryValue?: number,
    respirations?: number,
    pain?: number,
    pulse?: number,
    systolic?: number,
    diastolic?: number,
    pulseOximetryId?: number,
    spO2?: number
}

export interface ITimelinePatientResultEdit
{
    patientId: number,
    isCustomResult: boolean,
    resultIdentifier1: number,
    resultIdentifier2?: number,
    resultLabel: string,
    resultValue: string
}

export interface ITimelineResultPayloadItem
{
    fbSubId: number,
    fbSubLabel: string,
    template?: IFishboneTemplateType,
    value?: string,
    abnormal?: string
}

export interface ITimelineNewResultPayload
{
    patientId: number,
    date: NoTzDate,
    isCustomResult?: boolean,
    resultType?: number, // fbId for fishbone result or globalEntityId for custom result
    resultItems: ITimelineResultPayloadItem[]
}

export interface ITimelineResultType
{
    isCustomResult: boolean,
    id: number,
    displayName: string
}

export interface ITimelineResultAbnormal
{
    value: string,
    label: string
}

export enum TimelineResultAbnormal {
    Normal = "",
    Below = "Below",
    Low = "L",
    ReallyLow = "LL",
    Above = "Above",
    High = "H",
    ReallyHigh = "HH",
}

export const ResultAbnormalOptions: ITimelineResultAbnormal[] = [
    { value: "", label: "" },
    { value: "Below", label: "Below" },
    { value: "L", label: "Low (L)" },
    { value: "LL", label: "Really Low (LL)" },
    { value: "Above", label: "Above" },
    { value: "H", label: "High (H)" },
    { value: "HH", label: "Really High (HH)" },
]

export enum MeasurementSystem {
    Imperial = "E",
    Metric = "M"
}

export enum IEMRDateRangeOption {
    Three = "3",
    Six = "6",
    Year = "12",
    TwoYear = "24",
    All = "*"
}

export enum EMRSummaryTimelineSettings {
    Table = "TABLE",
    ResultExpanded = "RE",
    VitalsExpanded = "VE",
    LookbackRange = "PREVM",
    VitalsParameter = "AMPV"
}

export enum EMRSummaryTimelineParameterOrder {
    BpDiastolicLow = 1,
    BpDiastolicHigh = 2,
    BpSystolicLow = 3,
    BpSystolicHigh = 4,
    BmiLow = 5,
    BmiHigh = 6
}

export enum TimelineResultTemplateControlTypes {
    Checkbox = 1,
    Numeric = 3,
    Date = 5
}